import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Intl } from '../../../constants';
import { ExclamationCircleFilled, QuestionCircleFilled, Tooltip } from '@fx-ui/fine-design';
import { find, get, map, size, useCacheCallback } from '@libs/shared';
import {
    PLUGIN_ARGUMENT_FIELD_TYPE,
    IFunctionArgumentValueField,
    PluginArgumentValue,
    PLUGIN_ARGUMENT_VALUE_FROM,
    IPluginArgumentField,
    IFunctionArgumentFrom,
    ParameterMatchMetadata
} from '../../../interfaces';
import { IFunctionParameterConfigProps } from '../../open-plugin/components';
import {
    PluginDatetime,
    PluginDept,
    PluginMember,
    PluginNumber,
    PluginText,
    PluginVector,
    PluginSelect
} from '../';
import { CustomCombo, EnhanceNavIcon } from '../../../components';

interface PluginComponentContextValue
    extends Pick<
        IFunctionParameterConfigProps,
        'onParameterFieldSelectorRender' | 'onParameterExprSelectorRender'
    > {
    isView?: boolean;
    field: IPluginArgumentField;
    fieldValue: PluginArgumentValue;
    /** 可选字段列表 */
    optionalFields?: IFunctionArgumentValueField[];
    checkStatusVisible?: boolean;
    supportVectorLoop?: boolean;
    resolverCtx?: string;
    checkTipVisible?: boolean;
    defaultResolverType?: PLUGIN_ARGUMENT_VALUE_FROM;
    /**
     * @param v resolver_arg
     * @param others Omit<IPluginArgumentValue,resolver_arg>
     */
    onChange: (v: any, others?: any) => void;
    onFieldSelect?: (argumentId: string, fieldId: string) => void;
}

interface IPluginComponent extends Omit<PluginComponentContextValue, 'onChange'> {
    className?: string;
    hideLabel?: boolean;
    /** 可选赋值方式 */
    resolverTypes?: PLUGIN_ARGUMENT_VALUE_FROM[];
    resolverTypeList?: IFunctionArgumentFrom[];
    hideResolverTypeSelect?: boolean;
    onChange: (v: PluginArgumentValue) => void;
    /** 当前参数的匹配元数据 */
    matchMetadata?: ParameterMatchMetadata;
    /** 所有参数的匹配元数据（用于传递给 vector 子字段） */
    autoFillMetadata?: Record<string, ParameterMatchMetadata>;
}

const PluginComponentContext = React.createContext<PluginComponentContextValue | null>(null);
PluginComponentContext.displayName = 'PluginComponentContext';
export { PluginComponentContext };

/**
 * 插件组件
 */
const PluginComponent: React.FC<IPluginComponent> = (props) => {
    const {
        className,
        hideLabel,
        field,
        fieldValue,
        isView,
        resolverTypes,
        resolverTypeList,
        hideResolverTypeSelect,
        resolverCtx = '',
        onChange,
        matchMetadata,
        autoFillMetadata,
        ...otherProps
    } = props;

    const handleValueChange = useCacheCallback((resolverArg, otherParams) => {
        onChange({
            ...fieldValue,
            resolver_arg: resolverArg,
            ...otherParams
        } as any as PluginArgumentValue);
    });

    const argumentField: IPluginArgumentField = useMemo(() => {
        return {
            ...field,
            isEnabled: field.isEnabled ?? true
        };
    }, [field]);

    const { label, description, isRequired, isEnabled, fieldType } = argumentField;

    const comboItems = useMemo(() => {
        const methodList =
            resolverTypes ||
            get(
                find(resolverTypeList, (v) => {
                    return v.field_type === fieldType;
                }),
                'resolver_types',
                []
            );
        return map(methodList, (value) => {
            if (value === PLUGIN_ARGUMENT_VALUE_FROM.LITERAL) {
                return { text: Intl.get('plugin.value.type.literal'), value };
            }
            if (value === PLUGIN_ARGUMENT_VALUE_FROM.PROCESS_VAR) {
                return { text: Intl.get('plugin.value.type.process_var'), value };
            }
            if (value === PLUGIN_ARGUMENT_VALUE_FROM.EXPR) {
                return { text: Intl.get('plugin.value.type.expr'), value };
            }
            return { text: '', value: '' };
        });
    }, [resolverTypeList, resolverTypes, fieldType]);

    const hideResolverTypeCombo = size(comboItems) === 1 || hideResolverTypeSelect;
    const isVector = fieldType === PLUGIN_ARGUMENT_FIELD_TYPE.VECTOR;
    const currentResolverType = get(
        fieldValue,
        'resolver_type',
        isVector ? PLUGIN_ARGUMENT_VALUE_FROM.VECTOR : PLUGIN_ARGUMENT_VALUE_FROM.LITERAL
    );

    const renderField = () => {
        switch (fieldType) {
            case PLUGIN_ARGUMENT_FIELD_TYPE.TEXT:
                return <PluginText />;
            case PLUGIN_ARGUMENT_FIELD_TYPE.NUMBER:
                return <PluginNumber />;
            case PLUGIN_ARGUMENT_FIELD_TYPE.DATETIME:
                return <PluginDatetime />;
            case PLUGIN_ARGUMENT_FIELD_TYPE.SELECT:
                return <PluginSelect />;
            case PLUGIN_ARGUMENT_FIELD_TYPE.USER:
                return <PluginMember />;
            case PLUGIN_ARGUMENT_FIELD_TYPE.DEPT:
                return <PluginDept />;
            case PLUGIN_ARGUMENT_FIELD_TYPE.VECTOR:
                return (
                    resolverTypeList && (
                        <PluginVector
                            resolverTypeList={resolverTypeList}
                            autoFillMetadata={autoFillMetadata}
                        />
                    )
                );
        }
    };

    // 渲染匹配状态图标
    const renderMatchStatusIcon = () => {
        // 如果没有匹配元数据，不显示任何图标
        if (
            !matchMetadata ||
            !matchMetadata.matched ||
            field.fieldType === PLUGIN_ARGUMENT_FIELD_TYPE.VECTOR
        ) {
            return null;
        }

        const { matched, hasDuplicateLabel } = matchMetadata;

        const renderDuplicateTip = () => {
            if (!matched || !hasDuplicateLabel) {
                return null;
            }
            return (
                <Tooltip content={Intl.get('fxp.app.form_design.fill_confirm.tip')} placement="top">
                    <ExclamationCircleFilled className="filled-icon same-name" />
                </Tooltip>
            );
        };

        const renderSuccessTip = () => {
            if (!matched) {
                return null;
            }
            return <EnhanceNavIcon size={16} className="filled-icon" />;
        };

        return (
            <>
                {renderDuplicateTip()}
                {renderSuccessTip()}
            </>
        );
    };

    return (
        <div className={classNames('fx-plugin-component', className)}>
            {label && !hideLabel && (
                <div className="label">
                    <span className="text">
                        {isRequired && <span className="asterisk">*</span>}
                        {label}
                    </span>
                    {description && (
                        <Tooltip placement="top" content={description} effect="light">
                            <QuestionCircleFilled className="tip-icon" />
                        </Tooltip>
                    )}
                </div>
            )}
            <div
                className={classNames('content', {
                    'hide-label': hideLabel,
                    'is-filled': !!matchMetadata
                })}
            >
                {!hideResolverTypeCombo && (
                    <div className="left-combo">
                        <CustomCombo
                            className="resolver-type-combo"
                            isView={isView}
                            hasSearch={false}
                            hasClear={false}
                            dropdownMatchSelectWidth={false}
                            disabled={!isEnabled}
                            items={comboItems}
                            value={currentResolverType}
                            onChange={(v: PLUGIN_ARGUMENT_VALUE_FROM) => {
                                if (v !== currentResolverType) {
                                    let emptyValue;
                                    switch (v) {
                                        case PLUGIN_ARGUMENT_VALUE_FROM.EXPR:
                                        case PLUGIN_ARGUMENT_VALUE_FROM.LITERAL:
                                            emptyValue = '';
                                            break;
                                        case PLUGIN_ARGUMENT_VALUE_FROM.PROCESS_VAR:
                                        case PLUGIN_ARGUMENT_VALUE_FROM.VECTOR:
                                            emptyValue = null;
                                            break;
                                    }
                                    onChange({
                                        ...fieldValue,
                                        resolver_type: v,
                                        resolver_arg: emptyValue
                                    } as any as PluginArgumentValue);
                                }
                            }}
                        />
                    </div>
                )}
                <PluginComponentContext.Provider
                    value={{
                        field: argumentField,
                        fieldValue,
                        isView,
                        resolverCtx,
                        onChange: handleValueChange,
                        ...otherProps
                    }}
                >
                    <div className="right-content">{renderField()}</div>
                </PluginComponentContext.Provider>
                {renderMatchStatusIcon()}
            </div>
        </div>
    );
};

export default PluginComponent;
